import { useEffect, useState } from "react";
import { CaretDown, CheckCircle, ArrowsClockwise, XCircle, Circle, ChatText, SealCheck, Play, Pause, SpinnerGap } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { ReactionThread } from "@/components/ReactionThread";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUSES = [
  ["lancar", "Lancar", "bg-green-600", "text-green-600", CheckCircle],
  ["ulang", "Ulang", "bg-yellow-500", "text-yellow-600", ArrowsClockwise],
  ["belum", "Belum", "bg-red-500", "text-red-600", XCircle],
];
const META = Object.fromEntries(STATUSES.map(([v, l, bg, tone, Icon]) => [v, { l, bg, tone, Icon }]));

export function TahfidzPanel({ studentId, mode, onSaved }) {
  const { user } = useAuth();
  const isSdqu = user?.school === "sdqu";
  const JUZ = [30, 29, 28, 27, 26];
  const [juz, setJuz] = useState(30);
  const [surah, setSurah] = useState([]);
  const [progress, setProgress] = useState([]);
  const [openNum, setOpenNum] = useState(null);
  const [ayatCache, setAyatCache] = useState({});
  const [drafts, setDrafts] = useState({});
  const [audio] = useState(() => new Audio());
  const [playing, setPlaying] = useState(null);
  const [loadingA, setLoadingA] = useState(null);
  const [adminAudio, setAdminAudio] = useState({});

  useEffect(() => {
    audio.onended = () => setPlaying(null);
    return () => { audio.pause(); };
  }, [audio]);

  const toggleAudio = (g, adminUrl) => {
    const id = adminUrl || g;
    if (playing === id) { audio.pause(); setPlaying(null); return; }
    audio.pause();
    audio.src = adminUrl || `https://cdn.islamic.network/quran/audio/128/ar.alafasy/${g}.mp3`;
    setLoadingA(id);
    audio.play().then(() => { setPlaying(id); setLoadingA(null); })
      .catch(() => { setLoadingA(null); toast.error("Gagal memutar audio"); });
  };

  const loadProgress = () => { if (studentId) api.get(`/progress?student_id=${studentId}&category=tahfidz`).then((r) => setProgress(r.data)); };
  useEffect(() => { api.get(`/tahfidz/surah${isSdqu ? `?juz=${juz}` : ""}`).then((r) => setSurah(r.data)); setOpenNum(null); }, [juz, isSdqu]);
  useEffect(() => { loadProgress(); /* eslint-disable-next-line */ }, [studentId]);

  const byKey = {};
  progress.forEach((p) => { byKey[p.item_key] = p; });

  const toggle = async (num) => {
    if (openNum === num) { setOpenNum(null); return; }
    setOpenNum(num);
    if (adminAudio[num] === undefined) {
      api.get(`/tahfidz/audio?surah=${num}`).then((r) => setAdminAudio((c) => ({ ...c, [num]: r.data }))).catch(() => {});
    }
    if (!ayatCache[num]) {
      try {
        const res = await fetch(`https://api.alquran.cloud/v1/surah/${num}/editions/quran-uthmani,id.indonesian`);
        const j = await res.json();
        const ar = j.data?.[0]?.ayahs || []; const idn = j.data?.[1]?.ayahs || [];
        setAyatCache((c) => ({ ...c, [num]: ar.map((a, i) => ({ n: a.numberInSurah, g: a.number, text: a.text, arti: idn[i]?.text })) }));
      } catch (e) { toast.error("Gagal memuat ayat"); }
    }
  };

  const setStatus = async (key, label, status) => {
    try { await api.post("/progress", { student_id: studentId, category: "tahfidz", item_key: key, item_label: label, status }); loadProgress(); onSaved && onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveComment = async (key, label) => {
    const comment = (drafts[key] ?? "").trim();
    if (!comment) return;
    try { await api.post("/progress/comment", { student_id: studentId, category: "tahfidz", item_key: key, item_label: label, comment }); toast.success("Komentar terkirim"); loadProgress(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const markResponded = async (key) => {
    try { await api.post("/progress/responded", { student_id: studentId, category: "tahfidz", item_key: key }); toast.success("Ditandai sudah direspons"); loadProgress(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-2">
      {isSdqu && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="tahfidz-juz-tabs">
          {JUZ.map((j) => (
            <button key={j} data-testid={`tahfidz-juz-${j}`} onClick={() => setJuz(j)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold ${juz === j ? "bg-primary text-white shadow" : "bg-white border border-stone-200 text-stone-500"}`}>Juz {j}</button>
          ))}
        </div>
      )}
      {surah.map((s) => {
        const isOpen = openNum === s.num;
        const done = Array.from({ length: s.ayat }).filter((_, i) => byKey[`${s.num}:${i + 1}`]?.status === "lancar").length;
        return (
          <div key={s.num} className="bg-white rounded-2xl border border-stone-200/60 shadow-sm overflow-hidden">
            <button data-testid={`surah-${s.num}`} onClick={() => toggle(s.num)} className="w-full p-3.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-primary font-bold text-xs">{s.num}</span>
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-stone-800 text-sm">{s.name}</p>
                <p className="text-stone-400 text-xs">{s.ayat} ayat • {done} lancar</p>
              </div>
              <CaretDown size={18} className={`text-stone-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {isOpen && (
              <div className="px-3 pb-3 space-y-2 border-t border-stone-100 pt-2">
                {!ayatCache[s.num] ? (
                  <p className="text-center text-stone-400 text-sm py-3">Memuat ayat...</p>
                ) : ayatCache[s.num].map((a) => {
                  const key = `${s.num}:${a.n}`;
                  const p = byKey[key];
                  const m = p?.status ? META[p.status] : null;
                  const aUrl = adminAudio[s.num]?.[a.n];
                  const aid = aUrl || a.g;
                  return (
                    <div key={key} data-testid={`ayat-${key}`} className="bg-stone-50 rounded-xl p-3">
                      <div className="flex items-start gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center shrink-0 mt-1">{a.n}</span>
                        <p className="font-arabic text-2xl leading-loose text-right flex-1 text-stone-800" dir="rtl">{a.text}</p>
                        <button data-testid={`play-${key}`} onClick={() => toggleAudio(a.g, aUrl)}
                          className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-1 active:scale-90 transition-transform">
                          {loadingA === aid ? <SpinnerGap size={18} weight="bold" className="text-green-600 animate-spin" />
                            : playing === aid ? <Pause size={18} weight="fill" className="text-green-600" />
                            : <Play size={18} weight="fill" className="text-green-600" />}
                        </button>
                      </div>
                      {a.arti && <p data-testid={`arti-${key}`} className="text-stone-600 text-xs mb-2 leading-relaxed">{a.arti}</p>}

                      {mode === "input" ? (
                        <div className="flex gap-1.5">
                          {STATUSES.map(([val, lbl, bg, tone, Icon]) => (
                            <button key={val} data-testid={`ayat-status-${key}-${val}`} onClick={() => setStatus(key, `${s.name} : ${a.n}`, val)}
                              className={`flex-1 py-1.5 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 ${p?.status === val ? `${bg} text-white` : "bg-white border border-stone-200 text-stone-500"}`}>
                              <Icon size={12} weight="fill" /> {lbl}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 mb-1">
                          {m ? <m.Icon size={16} weight="fill" className={m.tone} /> : <Circle size={16} className="text-stone-300" />}
                          <span className={`text-[11px] font-bold ${m ? m.tone : "text-stone-400"}`}>{m ? m.l : "Belum dinilai"}</span>
                        </div>
                      )}
                      <ReactionThread studentId={studentId} category="tahfidz" itemKey={key} itemLabel={`${s.name} : ${a.n}`} thread={p?.thread || []} onPosted={loadProgress} canEdit={user?.role === "admin" || user?.role === "guru"} />


                      {/* Parent comment */}
                      {false && p?.comment && (
                        <div className="mt-2 bg-white rounded-lg p-2 border border-stone-100">
                          <p className="text-[10px] text-stone-400 flex items-center gap-1"><ChatText size={12} weight="fill" /> Komentar Orang Tua</p>
                          <p className="text-stone-700 text-xs">{p.comment}</p>
                          {mode === "input" ? (
                            <button data-testid={`respond-${key}`} onClick={() => markResponded(key)} disabled={p.responded}
                              className={`mt-2 text-[11px] font-semibold px-3 py-1 rounded-full flex items-center gap-1 ${p.responded ? "bg-green-100 text-green-700" : "bg-primary text-white active:scale-95 transition-transform"}`}>
                              <SealCheck size={13} weight="fill" /> {p.responded ? "Sudah Direspons" : "Tandai Sudah Direspons"}
                            </button>
                          ) : p.responded && (
                            <p className="mt-1 text-[10px] text-green-600 font-semibold flex items-center gap-1"><SealCheck size={12} weight="fill" /> Sudah dibaca guru</p>
                          )}
                        </div>
                      )}

                      {/* Parent writes comment */}
                      {false && mode === "view" && (
                        <div className="mt-2 flex gap-2">
                          <input data-testid={`comment-input-${key}`} value={drafts[key] ?? (p?.comment || "")}
                            onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                            placeholder="Tulis komentar untuk guru..."
                            className="flex-1 min-w-0 bg-white border border-stone-200 rounded-lg py-1.5 px-2 text-xs outline-none focus:border-primary" />
                          <button data-testid={`comment-save-${key}`} onClick={() => saveComment(key, `${s.name} : ${a.n}`)}
                            className="text-xs font-semibold bg-primary text-white px-3 rounded-lg active:scale-95 transition-transform">Kirim</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
